import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { adminApi } from "@/lib/api/admin-api";
import { getApiErrorMessage } from "@/lib/api/api";
import { recipeKeys } from "@/hooks/recipes/keys";
import type { Material } from "@/types/material";

/** Past this many, the list of recipes is cut short with "and N more". */
const MAX_LISTED_RECIPES = 8;

interface DeleteMaterialDialogProps {
  /** The ingredient being deleted; null keeps the dialog closed. */
  material: Material | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirms deleting an ingredient (T-1401). Before the button is live it asks
 * the API which recipes use the ingredient, and when any do it lists them: the
 * delete removes the ingredient from those recipes too, so the person deleting
 * it should see what they are about to change.
 */
export function DeleteMaterialDialog({ material, onOpenChange }: DeleteMaterialDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const materialId = material?.id;
  const name = material?.name ?? "";

  const usage = useQuery({
    queryKey: ["materials", "detail", materialId, "usage"],
    queryFn: () => adminApi.getMaterialUsage(materialId!),
    enabled: materialId !== undefined,
    // Always ask again when the dialog opens: a stale "unused" is the one
    // answer that must not be shown.
    staleTime: 0,
    gcTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminApi.deleteMaterial(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["materials"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "materials"] });
      void queryClient.invalidateQueries({ queryKey: ["entity-select", "materials"] });
      // The recipes that used it have lost a line, and their cost with it.
      void queryClient.invalidateQueries({ queryKey: recipeKeys.all });
      toast.success(t`Material deleted`);
      onOpenChange(false);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to delete material`)),
  });

  const recipes = usage.data?.recipes ?? [];
  const parents = usage.data?.parentMaterials ?? [];
  const listed = recipes.slice(0, MAX_LISTED_RECIPES).map((recipe) => recipe.name);
  const unlisted = recipes.length - listed.length;
  if (unlisted > 0) listed.push(t`and ${unlisted} more`);

  let description: string;
  if (usage.isPending) {
    description = t`Checking which recipes use ${name}…`;
  } else if (recipes.length > 0) {
    const recipeCount = plural(recipes.length, { one: "# recipe", other: "# recipes" });
    description = t`${name} is used in ${recipeCount}. Deleting it also removes it from:`;
  } else {
    description = t`This will delete ${name} from your organization.`;
  }

  const cannotUndo = t`This action cannot be undone`;
  const notes: string[] = [];
  if (usage.isError) {
    notes.push(
      getApiErrorMessage(usage.error, t`Could not check which recipes use this ingredient.`),
    );
  }
  if (parents.length > 0) {
    const parentNames = parents.map((parent) => parent.name).join(", ");
    notes.push(t`It is also removed from the compound ingredients that contain it: ${parentNames}.`);
  }
  // With recipes listed, the bullets are the recipes and this moves below them.
  if (recipes.length > 0) notes.push(cannotUndo);

  return (
    <ConfirmDeleteDialog
      open={!!material}
      onOpenChange={(open) => {
        if (!open) onOpenChange(false);
      }}
      title={t`Delete material?`}
      description={description}
      bullets={recipes.length > 0 ? listed : [cannotUndo]}
      note={notes.length > 0 ? notes.join(" ") : undefined}
      confirmLabel={t`Delete material`}
      onConfirm={() => {
        if (materialId !== undefined) deleteMutation.mutate(materialId);
      }}
      isPending={deleteMutation.isPending}
      confirmDisabled={usage.isPending}
    />
  );
}
